import * as React from 'react'
import { ReactElement, useEffect, useRef } from 'react'
import { IComboBox, Stack, StackItem } from '@fluentui/react'
import {
    InvoiceAction,
    InvoiceListItemDto,
    InvoiceMutationDto,
    InvoiceStatus,
    InvoiceTemplateDto,
    InvoiceType,
    LineItemGroupMutationDto,
    LineItemGroupType,
    LineItemMutationDto,
    LineItemType,
    MetaDataDto,
    PrefillDeterminer,
    PrefillPeriod,
} from '../../../../config/finance/invoiceConfig'
import FileDropdown from '../../../Dropdown/File/FileDropdown'
import FormikDropdown from '../../../../components/Formik/Dropdown/FormikDropdown'
import { useFormikContext } from 'formik'
import { defaultInvoiceValues } from '../../../../pages/Finance/Invoice/AddInvoice'
import { getDropdownProps } from '../../../../helpers/formHelper'
import { InvoiceProps } from './InvoiceForm'
import { useDispatch, useSelector } from 'react-redux'
import { Store } from '../../../../reducers'
import useAxios from '../../../../hooks/useAxios'
import { DataPromise, shouldDispatchLoad } from '../../../../context/AxiosContext'
import * as UUID from 'uuid'
import { PricePrecedence } from '../../../../config/finance/financeConfig'
import { LineItemEntities, LineItemEntity } from '../../../../hooks/finance/invoice/useLineItemEntities'
import { DateTime, DateTimeUnit, Interval } from 'luxon'
import { useTranslation } from 'react-i18next'

const PeriodKeyMap = new Map<PrefillPeriod, DateTimeUnit>([
    [PrefillPeriod.Year, 'year'],
    [PrefillPeriod.Month, 'month'],
    [PrefillPeriod.Week, 'week'],
])

export function getFilterInterval(
    period: PrefillPeriod,
    offset: PrefillDeterminer,
    cutoff: PrefillDeterminer,
): Interval {
    const now = DateTime.local()
    const periodKey = PeriodKeyMap.get(period)

    const getDeterminerValue = (determiner: PrefillDeterminer, target: DateTime | false, boundary: DateTime) => {
        if (!target) {
            return boundary
        }
        switch (determiner) {
            case PrefillDeterminer.Previous:
                return target.minus({ [periodKey]: 1 })
            case PrefillDeterminer.CurrentIfMost:
                const twoThirds = now.startOf(periodKey).plus({ [periodKey]: 2 / 3 })
                return now >= twoThirds ? target : target.minus({ [periodKey]: 1 })
            case PrefillDeterminer.Current:
                return target
            case PrefillDeterminer.All:
                return boundary
        }
    }

    // If no period key has been set, we'll default to the boundary, which is every date with a 4-digit year.
    // Default period key is year, because the size of the boundary is exactly 10.000 years.
    return Interval.fromDateTimes(
        getDeterminerValue(offset, periodKey && now, DateTime.fromISO('0000-01-01')).startOf(periodKey || 'year'),
        getDeterminerValue(cutoff, periodKey && now, DateTime.local().endOf('year')).endOf(periodKey || 'year'),
    )
}

export const filterEntities = (
    entities: LineItemEntity[],
    period: PrefillPeriod,
    offset: PrefillDeterminer,
    cutoff: PrefillDeterminer,
): LineItemEntity[] => {
    const interval = getFilterInterval(period, offset, cutoff)
    return (entities || []).filter(entity => interval.contains(DateTime.fromISO(entity.date)))
}

const filterEmpty = obj =>
    Object.fromEntries(
        Object.entries(obj || {})
            .filter(([, value]) => ![undefined, null].includes(value))
            .map(([key, value]) => [
                key,
                typeof value === 'object' && !Array.isArray(value) ? filterEmpty(value) : value,
            ]),
    )

export const mapLineItemGroupTemplate = (
    template: LineItemGroupType,
    entities?: LineItemEntities,
): LineItemGroupMutationDto => {
    const lineItems = template.prefillType && entities ? mapLineItems(template, entities) : []
    const precedence = template.precedence ?? PricePrecedence.Inherited

    // Only create a line item group when we need to prefill, but it doesn't contain any of the prefill items.
    if (template.prefillType && lineItems.length === 0 && precedence === PricePrecedence.Inherited) {
        return undefined
    }

    return {
        id: UUID.v4(),
        templateGroupId: template.id,
        precedence,
        lineItems,
        description: template.description,
        price: template.price,
        vatType: template.vatType,
        showStatement: template.showStatement,
        consolidation: template.consolidation,
        accounting: template.accounting,
    }
}

export const mapLineItems = (template: LineItemGroupType, entities: LineItemEntities): LineItemMutationDto[] =>
    filterEntities(
        [
            ...(entities.orphans.get(template.prefillType) || []),
            ...(entities.selection.get(template.prefillType) || []),
        ],
        template.prefillPeriod,
        template.prefillOffset,
        template.prefillCutoff,
    )
        .filter(
            entity =>
                template.prefillType !== LineItemType.Invoice ||
                // Only ever prefill deposit invoices.
                (entity as InvoiceListItemDto).type === InvoiceType.Deposit,
        )
        .map(entities.map)
        .filter(Boolean)

export const mapInvoiceTemplate = (
    template: InvoiceTemplateDto,
    values: Partial<InvoiceMutationDto>,
    entities?: LineItemEntities,
): InvoiceMutationDto => {
    return {
        ...filterEmpty(defaultInvoiceValues),
        ...filterEmpty(values),
        ...filterEmpty(template),
        id: undefined,
        name: values?.name,
        fileId: values?.fileId,
        lineItemGroups:
            template?.lineItemGroups
                ?.map(groupTemplate => mapLineItemGroupTemplate(groupTemplate, entities))
                .filter(Boolean) ||
            values.lineItemGroups ||
            defaultInvoiceValues.lineItemGroups,
    }
}

export default function InvoiceFile({
    invoice,
    useInitialInvoice,
    templates,
    lineItemEntities,
}: InvoiceProps): ReactElement {
    const axios = useAxios()
    const dispatch = useDispatch()
    const { t: translate } = useTranslation()

    const { errors, initialValues, values, setValues, setFieldValue } = useFormikContext<InvoiceMutationDto>()
    const meta = useSelector<Store, DataPromise<MetaDataDto>>(state => state.financeInvoice.meta)
    const context = useFormikContext<InvoiceMutationDto>()
    const fileRef = useRef<IComboBox>()

    useEffect(() => {
        if (fileRef.current && !invoice) {
            fileRef.current.focus(false)
        }
    }, [fileRef.current])

    useEffect(() => {
        if (!values.templateId && !useInitialInvoice) {
            setFieldValue('templateId', templates?.find(template => template.isDefault)?.id)
        }
    }, [])

    useEffect(() => {
        if (values.templateId && meta.loaded) {
            setValues(
                useInitialInvoice
                    ? initialValues
                    : mapInvoiceTemplate(
                          templates?.find(template => values.templateId === template.id),
                          values,
                          lineItemEntities,
                      ),
            )
        }
    }, [values.templateId, meta?.data?.file?.id])

    useEffect(() => {
        // When the file ID changes from the original invoice, we need to reload the metadata.
        if (shouldDispatchLoad(meta) && values.fileId) {
            dispatch({
                type: InvoiceAction.GetMetaData,
                payload: axios.get(
                    '/finance/invoice/meta/' + (useInitialInvoice ? invoice.id : 'file/' + values.fileId),
                ),
            })
        }
    }, [meta, invoice?.id, values.fileId])

    return (
        <Stack horizontal tokens={{ childrenGap: 'm' }}>
            {templates?.length > 0 && (
                <StackItem grow={1}>
                    <FormikDropdown
                        context={context}
                        label={translate('invoice.templateid.label')}
                        placeholder={translate('invoice.templateid.placeholder')}
                        fieldName="templateId"
                        options={templates
                            .sort((a, b) => a.name.localeCompare(b.name))
                            .map(template => ({
                                key: template.id,
                                text: template.name,
                            }))}
                        disabled={values.status !== InvoiceStatus.New}
                        required
                    />
                </StackItem>
            )}

            <StackItem grow={2}>
                <FileDropdown
                    initialValues={[values.fileId]}
                    {...getDropdownProps(values.fileId, errors?.fileId, value => setFieldValue('fileId', value))}
                    disabled={values.status !== InvoiceStatus.New}
                    componentRef={fileRef}
                />
            </StackItem>
        </Stack>
    )
}
